// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.

import React, { useState } from "react";
import {
  Router,
  Route,
  Redirect,
  Switch,
} from "react-router-dom";
import TeamsRoute from "./TeamsRoute.js";
import { ProvideAuth } from "../hooks/use-auth.js";
import Privacy from "./Privacy";
import TermsOfUse from "./TermsOfUse";
import Routes from "../config/routes";
import {
  Provider,
  teamsTheme,
  Flex,
  mergeThemes,
  teamsDarkTheme,
  teamsHighContrastTheme,
} from "@fluentui/react-northstar";
import { rootStore } from "../stores/models/Root";
import RootStoreContext from "../contexts/rootStoreContext";
import { useTeams, checkInTeams } from "msteams-react-base-component";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../config/analytics/AppInsights";
import History from "../config/history.js";

/**
 * The main app which handles the initialization and routing
 * of the app.
 */
function App() {
  const [theme, setTheme] = useState(null);
  const [themeString, setThemeString] = useState(null);

  let options = {};
  // This is almost a direct copy of the default code in the useTeams code in msteams-react-base-component, but 
  // changed so that when the theme is unknown, it does not default to standard "Default" teams theme
  options.setThemeHandler = ((themeName) => {
    setThemeString(themeName);
    switch (themeName) {
      case "dark":
        setTheme(teamsDarkTheme);
        break;
      case "contrast":
        setTheme(teamsHighContrastTheme);
        break;
      case "default":
        setTheme(teamsTheme);
        break;
      default:
        // We don't know what the theme is at this point, so don't set it
        break;
    }
  });

  if (!checkInTeams() && !theme) {
    // If we aren't running under teams, then use default theme
    setThemeString("default");
    setTheme(teamsTheme);
  }

  useTeams(options);

  let baseTheme = {
    siteVariables: {
      themeString,
    }
  };

  let newTheme = theme ? mergeThemes(theme, baseTheme) : mergeThemes(teamsTheme, baseTheme);

  // Display the app home page hosted in Teams
  if (!theme) {
    // Render nothing until we know we have a valid theme
    return (<React.Fragment></React.Fragment>);
  } else {
    return (
      <AppInsightsContext.Provider value={reactPlugin}>
        <Provider theme={newTheme}>
          <RootStoreContext.Provider value={rootStore}>
            <ProvideAuth>
              <Router history={History}>
                <Flex className={"app " + themeString} hAlign='center' vAlign='center'>
                  <Switch>
                    <Redirect exact from={"/"} to={"login"} />
                    {Routes.map((route, index) => (
                      <TeamsRoute
                        key={index}
                        path={route.path}
                        exact={route.exact}
                        pageTitle={route.pageTitle}
                        children={route.component}
                      />
                    ))}
                    <Route exact path="/privacy" component={Privacy} />
                    <Route exact path="/termsofuse" component={TermsOfUse} />
                    <Route exact path="/login" component={TeamsHostError} />
                    <Redirect from="*" to={"/"} />
                  </Switch>
                </Flex>
              </Router>
            </ProvideAuth>
          </RootStoreContext.Provider>
        </Provider>
      </AppInsightsContext.Provider>
    );
  }
}

/**
 * This component displays an error message in the
 * case when a page is not being hosted within Teams.
 */
class TeamsHostError extends React.Component {
  render() {
    return (
      <div>
        <h3 className="Error">Ready to debug your app within Teams...</h3>
      </div>
    );
  }
}

export default App;
